// WP-2.5 (M23 IN-1..5, S-07/S-08): Capture Inbox queries/mutations.
import {
  captureBulkActionResponseSchema,
  captureInboxListResponseSchema,
  captureItemSchema,
  captureRecordAllResponseSchema,
  captureRecordResponseSchema,
  type CaptureBulkActionResponse,
  type CaptureInboxListResponse,
  type CaptureItemResponse,
  type CaptureRecordAllResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const INBOX_KEY = ['inbox'] as const;
export const inboxItemKey = (id: string): readonly [string, string] => ['inbox', id] as const;

export function useInbox(): UseQueryResult<CaptureInboxListResponse> {
  return useQuery({
    queryKey: INBOX_KEY,
    queryFn: async () => captureInboxListResponseSchema.parse(await api.get('/api/v1/inbox')),
  });
}

/** IN-5: sidebar/bottom-nav badge — polled like AlertBanner so it stays live without a
 * dedicated push channel. */
export function useInboxBadgeCount(): number {
  const { data } = useQuery({
    queryKey: [...INBOX_KEY, 'badge'],
    queryFn: async () => captureInboxListResponseSchema.parse(await api.get('/api/v1/inbox')),
    refetchInterval: 60_000,
  });
  return data?.badgeCount ?? 0;
}

export function useInboxItem(id: string): UseQueryResult<CaptureItemResponse> {
  return useQuery({
    queryKey: inboxItemKey(id),
    queryFn: async () => captureItemSchema.parse(await api.get(`/api/v1/inbox/${id}`)),
  });
}

function useInvalidateInbox(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: INBOX_KEY });
  };
}

export function useRecordInboxItem(
  id: string,
): UseMutationResult<{ transactionId: string }, Error, void> {
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: async () =>
      captureRecordResponseSchema.parse(await api.post(`/api/v1/inbox/${id}/record`)),
    onSuccess: invalidate,
  });
}

/** WP-4.1 (AU-2, IN-4): "Record all" — every linked event in one call (all-or-nothing). */
export function useRecordAllInboxItem(
  id: string,
): UseMutationResult<CaptureRecordAllResponse, Error, void> {
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: async () =>
      captureRecordAllResponseSchema.parse(await api.post(`/api/v1/inbox/${id}/record-all`)),
    onSuccess: invalidate,
  });
}

export function useRejectInboxItem(id: string): UseMutationResult<void, Error, void> {
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: async () => {
      await api.post(`/api/v1/inbox/${id}/reject`);
    },
    onSuccess: invalidate,
  });
}

export function useBulkResolveInbox(): UseMutationResult<
  CaptureBulkActionResponse,
  Error,
  { ids: string[]; action: 'record' | 'reject' }
> {
  const invalidate = useInvalidateInbox();
  return useMutation({
    mutationFn: async (input) =>
      captureBulkActionResponseSchema.parse(await api.post('/api/v1/inbox/bulk', input)),
    onSuccess: invalidate,
  });
}
